import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Building2, CreditCard, Receipt, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  AI_DASHBOARD_ADDON_DEFINITIONS,
  formatAddonPrice,
  getAiDashboardAddonPricing,
  getAiDashboardAddonTotals,
  listEnabledAiDashboardAddons,
} from '../lib/aiDashboardAddons';
import { api, verticalAPI, workspaceAdminAPI } from '../services/api';
import { DomainSetup } from '../components/RebrandingModule';
import {
  WHITE_LABEL_ADDON,
  addonsWithWhiteLabel,
  attachCustomDomain,
  isPortalHost,
  normaliseHost,
} from '../lib/tenantCustomDomain';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { CardSkeleton } from '../components/common/States';

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <Card>
    <CardHeader>
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div>
          <CardTitle className="text-hr-lg">{title}</CardTitle>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const DEFAULT_SUBSCRIPTION_PLANS = [];
const EMPTY_USAGE_LIMITS = Object.freeze({});

const resolvePlanCode = (plans, currentValue = '') => {
  const normalized = String(currentValue || '').trim();
  if (normalized && plans.some((plan) => String(plan.code || '').trim() === normalized)) {
    return normalized;
  }
  return plans[0]?.code || '';
};

const normalizePlansPayload = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : [];
  return rows
    .map((plan) => ({
      id: plan?.id,
      code: String(plan?.code || '').trim(),
      name: String(plan?.name || '').trim(),
      monthly_price: Number(plan?.monthly_price || 0),
      yearly_price: Number(plan?.yearly_price || 0),
      pricing_controls: plan?.pricing_controls || {},
      usage_limits: plan?.usage_limits || {},
    }))
    .filter((plan) => Boolean(plan.code || plan.name))
    .map((plan) => ({
      ...plan,
      code: plan.code || `plan_${plan.id ?? 'unknown'}`,
      name: plan.name || plan.code || `Plan ${plan.id ?? ''}`.trim(),
    }));
};

const flattenTree = (nodes = [], level = 0, rows = []) => {
  nodes.forEach((node) => {
    rows.push({ ...node, _level: level });
    flattenTree(node.children || [], level + 1, rows);
  });
  return rows;
};

const formatPlanMoney = (value, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const aiAddonKeys = AI_DASHBOARD_ADDON_DEFINITIONS.map((addon) => addon.key);

const areStringArraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const SuperadminTenantCreatePage = ({ onBack }) => {
  // Fetched, never listed here. The copy that used to live in this file offered
  // "Startup" and "Enterprise", which are not verticals the registry has ever
  // known — creating a tenant with one stored a key nothing could resolve.
  const [verticalOptions, setVerticalOptions] = useState([{ key: 'generic', label: 'General' }]);

  const [loadingPlans, setLoadingPlans] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [tenantHierarchy, setTenantHierarchy] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [createdAdminCredentials, setCreatedAdminCredentials] = useState(null);
  const [portalDomain, setPortalDomain] = useState('');
  const [domainOutcome, setDomainOutcome] = useState(null);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [aiCenterEnabled, setAiCenterEnabled] = useState(true);
  const [selectedAiAddonKeys, setSelectedAiAddonKeys] = useState([]);
  const [formData, setFormData] = useState({
    creation_scope: 'root',
    parent_tenant_id: '',
    name: '',
    slug: '',
    admin_email: '',
    description: '',
    max_users: 10,
    plan: '',
    status: 'active',
    vertical: 'generic',
    isolation_level: 'shared',
    billing_account_id: '',
    billing_model: 'parent_paid',
    custom_domain: '',
  });

  // What the new workspace answers on with nothing configured. Shown beside the
  // custom-domain field because typing it in there is the mistake the field
  // invites, and the server rejects it with a 422 rather than a no-op.
  const portalHost = useMemo(() => {
    const slug = formData.slug.trim().toLowerCase();
    return slug && portalDomain ? `${slug}.${portalDomain}` : '';
  }, [formData.slug, portalDomain]);

  const selectedPlan = useMemo(
    () => subscriptionPlans.find((plan) => String(plan.code) === String(formData.plan)),
    [subscriptionPlans, formData.plan]
  );
  const selectedPlanUsageLimits = selectedPlan?.usage_limits || EMPTY_USAGE_LIMITS;
  const selectedPlanDefaultAiAddonKeys = useMemo(
    () => (Array.isArray(selectedPlanUsageLimits?.addons) ? selectedPlanUsageLimits.addons.filter((addon) => aiAddonKeys.includes(addon)) : []),
    [selectedPlanUsageLimits]
  );
  const selectedPlanAiDefaultEnabled = Boolean(
    selectedPlanUsageLimits?.ai_integration ?? aiAddonKeys.some((addonKey) => (selectedPlanUsageLimits?.addons || []).includes(addonKey))
  );
  const effectivePlans = Array.isArray(subscriptionPlans) ? subscriptionPlans : [];
  const selectedPlanCode = resolvePlanCode(effectivePlans, formData.plan);
  const tenantOptions = useMemo(() => flattenTree(tenantHierarchy), [tenantHierarchy]);
  const isChildTenantMode = formData.creation_scope === 'child';
  const selectedParent = useMemo(
    () => tenantOptions.find((tenant) => String(tenant.id) === String(formData.parent_tenant_id)),
    [tenantOptions, formData.parent_tenant_id]
  );
  const selectedPlanCurrency = selectedPlan?.pricing_controls?.currency || 'INR';
  const effectivePlanUsageLimits = useMemo(() => {
    const baseUsageLimits = selectedPlanUsageLimits || {};
    const baseAddons = Array.isArray(baseUsageLimits?.addons) ? baseUsageLimits.addons : [];
    const preservedNonAiAddons = baseAddons.filter((addon) => !aiAddonKeys.includes(addon));
    return {
      ...baseUsageLimits,
      ai_integration: aiCenterEnabled,
      addons: aiCenterEnabled
        ? [...preservedNonAiAddons, ...selectedAiAddonKeys]
        : preservedNonAiAddons,
    };
  }, [aiCenterEnabled, selectedAiAddonKeys, selectedPlanUsageLimits]);
  const selectedPlanAiAddons = useMemo(
    () => listEnabledAiDashboardAddons(effectivePlanUsageLimits),
    [effectivePlanUsageLimits]
  );
  const selectedPlanAddonMonthlyTotal = useMemo(
    () => selectedPlanAiAddons.reduce((sum, addon) => sum + Number(getAiDashboardAddonPricing(effectivePlanUsageLimits, addon.key).monthly_price || 0), 0),
    [effectivePlanUsageLimits, selectedPlanAiAddons]
  );
  const selectedPlanAddonYearlyTotal = useMemo(
    () => getAiDashboardAddonTotals(effectivePlanUsageLimits).yearly_price || 0,
    [effectivePlanUsageLimits]
  );
  const selectedPlanCurrentMonthlyTotal = useMemo(
    () => Number(selectedPlan?.monthly_price || 0) + Number(selectedPlanAddonMonthlyTotal || 0),
    [selectedPlan, selectedPlanAddonMonthlyTotal]
  );
  const selectedPlanCurrentYearlyTotal = useMemo(
    () => Number(selectedPlan?.yearly_price || 0) + Number(selectedPlanAddonYearlyTotal || 0),
    [selectedPlan, selectedPlanAddonYearlyTotal]
  );

  useEffect(() => {
    let cancelled = false;
    verticalAPI.listRegistry()
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.verticals || [];
        if (list.length) setVerticalOptions(list);
      })
      .catch(() => { /* the generic fallback above keeps the form usable */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        let plansResponse;
        let hierarchyResponse;
        let billingAccountsResponse;
        let brandingResponse;
        [plansResponse, hierarchyResponse, billingAccountsResponse, brandingResponse] = await Promise.all([
          workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
          workspaceAdminAPI.getTenantHierarchy(),
          workspaceAdminAPI.listBillingAccounts(),
          // Only for the portal suffix to label the custom-domain field with.
          // Swallowed rather than joined to the others' fate: losing a label is
          // not a reason to make the whole form unusable.
          api.get('/api/branding').catch(() => null),
        ]);
        if (mounted) setPortalDomain(String(brandingResponse?.data?.portal_domain || ''));
        let normalizedPlans = normalizePlansPayload(plansResponse?.data);

        // If DB has no plans yet, bootstrap default catalog then refetch.
        if (normalizedPlans.length === 0) {
          await workspaceAdminAPI.bootstrapDefaultPlanAndSeed();
          plansResponse = await workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true });
          normalizedPlans = normalizePlansPayload(plansResponse?.data);
        }

        const planCatalog = normalizedPlans;
        if (!mounted) return;
        setSubscriptionPlans(planCatalog);
        setTenantHierarchy(Array.isArray(hierarchyResponse?.data) ? hierarchyResponse.data : []);
        setBillingAccounts(Array.isArray(billingAccountsResponse?.data) ? billingAccountsResponse.data : []);
        setFormData((prev) => ({ ...prev, plan: resolvePlanCode(planCatalog, prev.plan) }));
      } catch (error) {
        if (!mounted) return;
        setSubscriptionPlans([]);
        setTenantHierarchy([]);
        setBillingAccounts([]);
        setFormData((prev) => ({ ...prev, plan: '' }));
      } finally {
        if (mounted) setLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isChildTenantMode || formData.parent_tenant_id || tenantOptions.length === 0) {
      return;
    }
    setFormData((prev) => ({ ...prev, parent_tenant_id: String(tenantOptions[0].id) }));
  }, [formData.parent_tenant_id, isChildTenantMode, tenantOptions]);

  useEffect(() => {
    setAiCenterEnabled(selectedPlanAiDefaultEnabled);
  }, [selectedPlanAiDefaultEnabled, selectedPlanCode]);

  useEffect(() => {
    setSelectedAiAddonKeys((prev) =>
      areStringArraysEqual(prev, selectedPlanDefaultAiAddonKeys) ? prev : selectedPlanDefaultAiAddonKeys
    );
  }, [selectedPlanDefaultAiAddonKeys, selectedPlanCode]);

  const toggleAiAddon = (addonKey, checked) => {
    setSelectedAiAddonKeys((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, addonKey]));
      }
      return prev.filter((item) => item !== addonKey);
    });
  };

  const handleCreate = async () => {
    const name = formData.name.trim();
    const slug = formData.slug.trim().toLowerCase();
    const adminEmail = formData.admin_email.trim().toLowerCase();
    if (!name || !slug || !adminEmail) {
      toast.error('Tenant name, slug, and admin email are required');
      return;
    }
    if (isChildTenantMode && !formData.parent_tenant_id) {
      toast.error('Parent tenant is required');
      return;
    }

    const customDomain = normaliseHost(formData.custom_domain);
    // Refused here as well as server-side. This one is worth catching early:
    // the tenant is created first and cannot be un-created, so a domain the API
    // was always going to reject would otherwise cost a wasted workspace.
    if (isPortalHost(customDomain, portalDomain)) {
      toast.error(
        `${customDomain} is part of the platform's own domain. This workspace already answers on `
        + `${portalHost || `<slug>.${portalDomain}`} with nothing to set up — this field is for a `
        + 'domain you own, such as hr.yourcompany.com.'
      );
      return;
    }

    try {
      setSaving(true);
      setDomainOutcome(null);
      const selectedPlanId = selectedPlan?.id ? Number(selectedPlan.id) : null;
      const planAddons = Array.isArray(selectedPlanUsageLimits?.addons)
        ? selectedPlanUsageLimits.addons
        : [];
      const planAiAddons = selectedAiAddonKeys.filter((addon) => aiAddonKeys.includes(addon));
      const nonAiAddons = planAddons.filter((addon) => !aiAddonKeys.includes(addon));
      const normalizedDefaultAddons = [...selectedPlanDefaultAiAddonKeys].sort().join('|');
      const normalizedSelectedAddons = [...planAiAddons].sort().join('|');
      const shouldOverrideAiCenter = aiCenterEnabled !== selectedPlanAiDefaultEnabled || normalizedDefaultAddons !== normalizedSelectedAddons;
      const baseAddons = aiCenterEnabled ? [...nonAiAddons, ...planAiAddons] : nonAiAddons;
      // White-label is folded into the one override rather than written by a
      // second call: `usage_limits_override` REPLACES the plan's addon list
      // rather than merging into it, so two writes would each undo the other.
      const addons = addonsWithWhiteLabel(baseAddons, Boolean(customDomain));
      const usageLimitsOverride = shouldOverrideAiCenter || addons.length !== baseAddons.length
        ? { ai_integration: aiCenterEnabled, addons }
        : null;
      const requestData = {
        name,
        slug,
        admin_email: adminEmail,
        parent_tenant_id: formData.parent_tenant_id ? Number(formData.parent_tenant_id) : null,
        isolation_level: formData.isolation_level,
        billing_account_id: formData.billing_account_id ? Number(formData.billing_account_id) : null,
        billing_model: formData.billing_model,
        plan_id: selectedPlanId,
        max_users: Number(formData.max_users || 0) || null,
      };
      const createdResponse = isChildTenantMode
        ? await workspaceAdminAPI.createSubtenant(Number(formData.parent_tenant_id), requestData)
        : await workspaceAdminAPI.createTenant({
            name,
            slug,
            admin_email: adminEmail,
            vertical: formData.vertical,
          });
      const createdTenant = createdResponse?.data || {};
      const createdTenantId = Number(createdResponse?.data?.id);

      if (!isChildTenantMode && createdTenantId && selectedPlanId) {
        await workspaceAdminAPI.assignTenantSubscription(createdTenantId, {
          plan_id: selectedPlanId,
        });
      }

      if (createdTenantId && selectedPlanId && usageLimitsOverride) {
        await workspaceAdminAPI.assignTenantSubscription(createdTenantId, {
          plan_id: selectedPlanId,
          usage_limits_override: usageLimitsOverride,
        });
      }

      if (createdTenantId && formData.status && formData.status !== 'active') {
        if (isChildTenantMode) {
          await workspaceAdminAPI.updateTenantNode(createdTenantId, {
            status: formData.status,
          });
        } else {
          await workspaceAdminAPI.updateTenant(createdTenantId, {
            name,
            slug,
            status: formData.status,
          });
        }
      }

      // The tenant is committed by now. Everything below is best-effort: a
      // domain that fails to attach is reported as its own failure and left to
      // be retried from Rebranding, because rolling the workspace back over it
      // would throw away the admin account and the subscription too.
      let domainFailure = null;
      if (createdTenantId && customDomain) {
        const outcome = await attachCustomDomain(workspaceAdminAPI, createdTenantId, customDomain);
        domainFailure = outcome.failedStep;
        setDomainOutcome({
          tenantId: createdTenantId,
          domain: customDomain,
          records: outcome.records,
          verifiedAt: outcome.verifiedAt,
          check: null,
          // The add-on has to actually be in force, which is either because the
          // plan already carried it or because the override that adds it was
          // written — and that override needs a plan to hang off. Without one
          // the domain is stored and nothing will ever serve it.
          whiteLabelGranted: outcome.ok
            && addons.includes(WHITE_LABEL_ADDON)
            && (!usageLimitsOverride || Boolean(selectedPlanId)),
          failedStep: outcome.failedStep,
          error: outcome.ok ? null : getErrorMessage(outcome.error, 'no reason given'),
        });
      }

      if (createdTenant?.tenant_admin_email) {
        setCreatedAdminCredentials({
          tenantSlug: createdTenant.slug || slug,
          email: createdTenant.tenant_admin_email,
          temporaryPassword: createdTenant.tenant_admin_temporary_password || '',
        });
      } else {
        setCreatedAdminCredentials(null);
      }

      if (domainFailure) {
        // Created, not fully configured. Said as one message so it cannot be
        // read as the whole thing having failed and retried from scratch.
        toast.warning(`Tenant created, but ${domainFailure} failed. Retry it from the workspace's Rebranding screen.`);
      } else {
        toast.success('Tenant created successfully');
      }
      setFormData((prev) => ({
        ...prev,
        name: '',
        slug: '',
        admin_email: '',
        description: '',
        custom_domain: '',
      }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create tenant'));
    } finally {
      setSaving(false);
    }
  };

  // Checks the records against live DNS and records the answer. Previously only
  // the workspace itself could do this, so a domain set from here stayed
  // unverified — and an unverified domain is stored but never served.
  const verifyDomain = async () => {
    if (!domainOutcome?.tenantId) return;
    try {
      setVerifyingDomain(true);
      const { data } = await workspaceAdminAPI.verifyTenantCustomDomain(domainOutcome.tenantId);
      setDomainOutcome((prev) => (prev ? {
        ...prev,
        check: data,
        records: data.records || prev.records,
        verifiedAt: data.ownership?.verified ? new Date().toISOString() : null,
      } : prev));
      if (data.status === 'live') toast.success(data.summary);
      else toast.warning(data.summary);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not check the DNS records'));
    } finally {
      setVerifyingDomain(false);
    }
  };

  if (loadingPlans) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tenants
          </Button>
        </div>
        <CardSkeleton rows={4} />
        <CardSkeleton rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tenants
        </Button>
      </div>

      <SectionCard
        icon={Building2}
        title="Identity"
        description="Placement and core details for the new organization."
      >
          <div className="space-y-2">
            <Label htmlFor="tenant-creation-scope">Tenant Placement</Label>
            <Select
              value={formData.creation_scope}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  creation_scope: value,
                  parent_tenant_id: value === 'child' ? prev.parent_tenant_id : '',
                }))
              }
            >
              <SelectTrigger id="tenant-creation-scope" className="w-full">
                <SelectValue placeholder="Select placement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="root">Root Tenant</SelectItem>
                <SelectItem value="child">Under Parent Tenant</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isChildTenantMode ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="tenant-parent">Parent Tenant</Label>
                <Select
                  value={formData.parent_tenant_id || '_none'}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, parent_tenant_id: value === '_none' ? '' : value }))}
                >
                  <SelectTrigger id="tenant-parent" className="w-full">
                    <SelectValue placeholder="Select parent tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Select parent tenant</SelectItem>
                    {tenantOptions.map((tenant) => (
                      <SelectItem key={tenant.id} value={String(tenant.id)}>
                        {`${'-- '.repeat(tenant._level)}${tenant.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                Parent Tenant: <span className="font-medium text-foreground">{selectedParent?.name || 'Not selected'}</span>
              </div>
            </>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-name">Organization Name</Label>
              <Input
                id="tenant-name"
                placeholder="Enter organization name"
                value={formData.name}
                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-slug">Slug</Label>
              <Input
                id="tenant-slug"
                placeholder="Enter unique slug"
                value={formData.slug}
                onChange={(event) => setFormData((prev) => ({ ...prev, slug: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-admin-email">Admin Email</Label>
              <Input
                id="tenant-admin-email"
                type="email"
                placeholder="admin@company.com"
                value={formData.admin_email}
                onChange={(event) => setFormData((prev) => ({ ...prev, admin_email: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-description">Description</Label>
              <Input
                id="tenant-description"
                placeholder="Enter description"
                value={formData.description}
                onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-custom-domain">
                Custom Domain <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="tenant-custom-domain"
                placeholder="hr.yourcompany.com"
                value={formData.custom_domain}
                onChange={(event) => setFormData((prev) => ({ ...prev, custom_domain: event.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                A domain the organization owns. It is stored now and served once they publish the
                DNS records shown after creation.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-portal-host">Workspace Address</Label>
              <Input
                id="tenant-portal-host"
                readOnly
                tabIndex={-1}
                className="bg-muted/40 text-muted-foreground"
                value={portalHost || (portalDomain ? `<slug>.${portalDomain}` : '')}
              />
              <p className="text-xs text-muted-foreground">
                Automatic, from the slug. Nothing to configure — do not enter this as a custom
                domain.
              </p>
            </div>
          </div>
      </SectionCard>

      <SectionCard
        icon={CreditCard}
        title="Plan &amp; Limits"
        description="Subscription plan, seat limits, and pricing summary."
      >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-max-users">Max Users</Label>
              <Input
                id="tenant-max-users"
                type="number"
                min={0}
                value={formData.max_users}
                onChange={(event) => {
                  const value = Number.parseInt(event.target.value, 10);
                  setFormData((prev) => ({ ...prev, max_users: Number.isNaN(value) ? 0 : value }));
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-plan">Plan</Label>
              <Select
                value={selectedPlanCode}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, plan: value }))}
              >
                <SelectTrigger id="tenant-plan" className="w-full">
                  <SelectValue placeholder="Select Plan" />
                </SelectTrigger>
                <SelectContent>
                {effectivePlans.length === 0 ? (
                  <SelectItem value="__no_plans_available" disabled>
                    No subscription plans available
                  </SelectItem>
                ) : effectivePlans.map((plan, index) => (
                  <SelectItem key={`${plan.code}-${plan.id ?? index}`} value={plan.code}>
                    {plan.name || plan.code}
                  </SelectItem>
                ))}
                </SelectContent>
              </Select>
            </div>
          </div>
      </SectionCard>

      <SectionCard
        icon={Receipt}
        title="Billing &amp; Access"
        description="Isolation, billing configuration, and lifecycle status."
      >
          {isChildTenantMode ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tenant-isolation">Isolation</Label>
                <Select
                  value={formData.isolation_level}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, isolation_level: value }))}
                >
                  <SelectTrigger id="tenant-isolation" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">shared</SelectItem>
                    <SelectItem value="isolated">isolated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant-billing-account">Billing Account</Label>
                <Select
                  value={formData.billing_account_id || '_none'}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, billing_account_id: value === '_none' ? '' : value }))}
                >
                  <SelectTrigger id="tenant-billing-account" className="w-full">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {billingAccounts.map((account) => (
                      <SelectItem key={account.id} value={String(account.id)}>
                        {account.account_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant-billing-model">Billing Model</Label>
                <Select
                  value={formData.billing_model}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, billing_model: value }))}
                >
                  <SelectTrigger id="tenant-billing-model" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent_paid">parent_paid</SelectItem>
                    <SelectItem value="subtenant_paid">tenant_paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="tenant-status">Status</Label>
            <select
              id="tenant-status"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.status}
              onChange={(event) => setFormData((prev) => ({ ...prev, status: event.target.value }))}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-vertical">Vertical</Label>
            <select
              id="tenant-vertical"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.vertical}
              onChange={(event) => setFormData((prev) => ({ ...prev, vertical: event.target.value }))}
            >
              {verticalOptions.map((option) => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">Provisions industry modules, terminology, navigation, roles &amp; workflows.</p>
          </div>
      </SectionCard>

      <SectionCard
        icon={Sparkles}
        title="Modules &amp; Access"
        description="AI Center add-ons and subscription pricing preview."
      >
          <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            <div>
              Selected Plan: <span className="font-medium text-foreground">{selectedPlan?.name || 'Unassigned'}</span>
            </div>
            {selectedPlan ? (
              <>
                <div className="mt-3 flex items-center justify-between rounded-lg border border-border bg-card px-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">AI Center</p>
                    <p className="text-xs text-muted-foreground">Turn AI Center add-ons on or off for this new organization/tenant.</p>
                  </div>
                  <Switch
                    checked={aiCenterEnabled}
                    onCheckedChange={(checked) => {
                      setAiCenterEnabled(checked);
                      if (checked && selectedAiAddonKeys.length === 0) {
                        setSelectedAiAddonKeys(selectedPlanDefaultAiAddonKeys);
                      }
                    }}
                  />
                </div>
                <div className="mt-2">
                  Base Price: <span className="font-medium text-foreground">{formatPlanMoney(selectedPlan.monthly_price, selectedPlanCurrency)}/month</span>
                </div>
                <div className="mt-1">
                  AI Center Add-on Uplift: <span className="font-medium text-foreground">{formatPlanMoney(selectedPlanAddonMonthlyTotal, selectedPlanCurrency)}/month</span>
                </div>
                <div className="mt-1">
                  Current Subscription Total: <span className="font-medium text-foreground">{formatPlanMoney(selectedPlanCurrentMonthlyTotal, selectedPlanCurrency)}/month</span>
                </div>
                <div className="mt-1">
                  Current Subscription Yearly: <span className="font-medium text-foreground">{formatPlanMoney(selectedPlanCurrentYearlyTotal, selectedPlanCurrency)}/year</span>
                </div>
                <div className="mt-3 space-y-2">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    AI Center Add-ons
                  </p>
                  {AI_DASHBOARD_ADDON_DEFINITIONS.length === 0 ? (
                    <p>No premium AI Center modules are enabled on this plan.</p>
                  ) : (
                    AI_DASHBOARD_ADDON_DEFINITIONS.map((addon) => {
                      const pricing = getAiDashboardAddonPricing(selectedPlan.usage_limits || {}, addon.key);
                      const checked = selectedAiAddonKeys.includes(addon.key);
                      return (
                        <div key={addon.key} className="rounded-lg border border-border bg-card px-3 py-2">
                          <label className="flex items-start gap-3">
                            <Checkbox
                              checked={checked}
                              disabled={!aiCenterEnabled}
                              onCheckedChange={(value) => toggleAiAddon(addon.key, Boolean(value))}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-foreground">{addon.label}</div>
                              <div className="text-xs text-muted-foreground">{addon.description}</div>
                            </div>
                          </label>
                          <div className="mt-1 text-xs text-foreground">
                            +{formatAddonPrice(pricing, 'monthly')}/month or +{formatAddonPrice(pricing, 'yearly')}/year
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : null}
          </div>

          {domainOutcome ? (
            <div className={`rounded-lg border p-4 text-sm space-y-3 ${
              domainOutcome.error ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/30'
            }`}>
              <div>
                <p className="font-medium text-foreground">Custom Domain — {domainOutcome.domain}</p>
                {domainOutcome.error ? (
                  <p className="mt-1 text-muted-foreground">
                    The tenant was created, but {domainOutcome.failedStep} failed:{' '}
                    {domainOutcome.error}. Nothing else was rolled back — set the domain from the
                    workspace’s Rebranding screen.
                  </p>
                ) : (
                  <p className="mt-1 text-muted-foreground">
                    Saved, and not yet live. The domain serves nothing until the records below are
                    published and the workspace verifies it.
                  </p>
                )}
              </div>

              {!domainOutcome.error && !domainOutcome.whiteLabelGranted ? (
                <p className="text-muted-foreground">
                  No plan was selected, so the white-label add-on could not be granted. Until it is,
                  the domain is stored but will not be served.
                </p>
              ) : null}

              {domainOutcome.records.length > 0 ? (
                <DomainSetup
                  records={domainOutcome.records}
                  verifiedAt={domainOutcome.verifiedAt}
                  check={domainOutcome.check}
                  onVerify={verifyDomain}
                  verifying={verifyingDomain}
                />
              ) : null}
            </div>
          ) : null}

          {createdAdminCredentials ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
              <p className="font-medium text-foreground">Tenant Admin Login</p>
              <p className="mt-1 text-muted-foreground">Tenant Slug: {createdAdminCredentials.tenantSlug}</p>
              <p className="mt-1 text-muted-foreground">Email: {createdAdminCredentials.email}</p>
              <p className="mt-1 text-muted-foreground">
                Temporary Password: {createdAdminCredentials.temporaryPassword}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onBack} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Tenant'
              )}
            </Button>
          </div>
      </SectionCard>
    </div>
  );
};

export default SuperadminTenantCreatePage;
